import * as $ from 'jquery';
import { DragInstance } from './DragInstance';
import { DropFeedbackType } from './DropFeedbackType';
import { EmbeddedCustomCursor, ICustomCursor } from './EmbeddedCustomCursor';
import { IDropTarget } from './IDropTarget';
import { MiscUtil } from '../utility/MiscUtil';

/// <summary>
/// A drop target which uses delegates to replace inheritance.
///
/// Each implementation has a default behavior so you can specify just the minimum
/// set of properties to customize your behavior.  This makes it simpler to add new
/// custom drag handling to elements without having to define a new DropTarget class.
///
/// Example:
/// <![CDATA[
///   var dropTarget = new DelegateDropTarget
///   {
///       CanAccept = dragInstance => dragInstance is MyCustomDragInstance,
///       OnAcceptDrop = dragInstance =>
///       {
///           this.DoSomethingWithDroppedObject(dragInstance);
///           return jQuery.DeferredData<bool>().Resolve(true);
///       },
///       OnGetDropCursor = dragInstance => CustomCursor.Copy
///   };
///   DragDropManager.AttachDropTarget(element, dropTarget);
/// ]]>
///
/// Note: For interface methods which return IDropTarget, an additional
/// 'self' parameter is passed to make it easier to use anonymous functions in
/// the object initializer list.
///
///  Example:
///
///  var dropTarget = new DelegateDropTarget
///  {
///      OnDragOver = (dragInstance, self) => self
///  };
///
/// </summary>

export class DelegateDropTarget implements IDropTarget {
  /// <summary>
  /// Optional. Provides a high level filter which short circuits DragOver and AcceptDrop if it returns false.
  /// Example:
  ///
  ///   var dropTarget = new DelegateDropTarget
  ///   {
  ///       CanAccept = dragInstance => dragInstance is MyCustomDragInstance,
  ///   };
  /// </summary>
  public canAccept: (_: DragInstance) => boolean;

  /// <summary>
  /// Optional. Pass through to IDropTarget.FeedbackType, defaults DropFeedbackUponEntry if not set.
  /// See IDropTarget for more details.
  /// Example:
  ///   var dropTarget = new DelegateDropTarget
  ///   {
  ///       OnFeedbackType = () => DropFeedbackType.DropFeedbackUponMouseMove,
  ///   };
  /// </summary>
  public onFeedbackType: () => DropFeedbackType;

  /// <summary>
  /// Optional. Return a valid DragInstance to indicate ability to handle this drag instance.
  /// The default implementation returns the 'self' drag instance.
  /// Example:
  ///   var dropTarget = new DelegateDropTarget
  ///   {
  ///       OnDragOver = (dragInstance, self) =>
  ///       {
  ///          this.UpdateDragOverStyling();
  ///          return self;
  ///       }
  ///   };
  /// </summary>
  public onDragOver: (_: DragInstance, __: IDropTarget) => IDropTarget;

  /// <summary>
  /// Optional.  Set if want to do custom handling on drag over completion.
  /// Example:
  ///   var dropTarget = new DelegateDropTarget
  ///   {
  ///       OnDragExit = dragInstance => this.RemoveDragOverStyling()
  ///   };
  /// </summary>
  public onDragExit: (_: DragInstance) => void;

  /// <summary>
  /// Optional.  Do something with the drag instance.
  /// Default implementation, returns a 'false' result.  This callback can be short circuited
  /// by filtering with CanAccept, so you can limit your handler to valid drag instances.
  /// Example:
  ///   var dropTarget = new DelegateDropTarget
  ///   {
  ///       OnAcceptDrop = dragInstance => this.HandleDrop(dragInstance)
  ///   };
  /// </summary>
  // TODO: replace JQuery.Deferred with Promise (jQuery 3.0+ is Promises/A+ compliant)
  public onAcceptDrop: (_: DragInstance) => JQuery.Deferred<boolean>;

  /// <summary>
  /// Optional. Allows changing the drop target for given object.  See IDropTarget for specifics.
  /// Example:
  ///   var dropTarget = new DelegateDropTarget
  ///   {
  ///       OnGetDropTarget = (hit, self) => self
  ///   };
  /// </summary>
  public onGetDropTarget: (_: JQuery, __: IDropTarget) => IDropTarget;

  /// <summary>
  /// Optional. Specify the drop cursor that most closely conveys the action you will be doing.
  /// Example:
  ///   var dropTarget = new DelegateDropTarget
  ///   {
  ///       OnGetDropCursor = dragInstance => CustomCursor.Copy,
  ///   };
  /// </summary>
  public onGetDropCursor: (_: DragInstance) => ICustomCursor;

  public get_feedbackType(): DropFeedbackType {
    return (!MiscUtil.IsNullOrUndefined(this.onFeedbackType)) ? this.onFeedbackType() : DropFeedbackType.dropfeedbackuponentry;
  }
  public dragOver(d: DragInstance): IDropTarget {
    if (!MiscUtil.IsNullOrUndefined(this.canAccept) && !this.canAccept(d)) {
      return null;
    }
    return (!MiscUtil.IsNullOrUndefined(this.onDragOver)) ? this.onDragOver(d, this) : this;
  }
  public dragExit(d: DragInstance): void {
    if (!MiscUtil.IsNullOrUndefined(this.onDragExit)) {
      this.onDragExit(d);
    }
  }
  public acceptDrop(d: DragInstance): JQuery.Deferred<boolean> {
    if (!MiscUtil.IsNullOrUndefined(this.canAccept) && !this.canAccept(d)) {
      return $.Deferred<boolean>().resolve(false);
    }
    return (!MiscUtil.IsNullOrUndefined(this.onAcceptDrop)) ? this.onAcceptDrop(d) : $.Deferred<boolean>().resolve(false);
  }
  public getDropTarget(hit: JQuery): IDropTarget {
    return (!MiscUtil.IsNullOrUndefined(this.onGetDropTarget)) ? this.onGetDropTarget(hit, this) : this;
  }
  public getDropCursor(currentDrag: DragInstance): ICustomCursor {
    return (this.onGetDropCursor !== null) ? this.onGetDropCursor(currentDrag) : EmbeddedCustomCursor.noOp;
  }
}
